import { useLuminanceGrid } from "./LuminanceGridContext";
import { useMemo } from "react";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";

export function LuminanceGridViewer() {
    const {
        viewBoxAttr,
        cells,
        canExport,
        handleDownload,
        imageDataUrl,
        effectiveCols,
        effectiveRows,
        blobOutlineEnabled,
        blobOutlineLayerGroups,
    } = useLuminanceGrid();

    const cellShapeUsesPaths = cells.some((c) => Boolean(c.pathD));

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(" ").map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    return (
        <div className="panel-viewer luminance-grid-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>
                <div className="svg-scroll-wrapper">
                    {!imageDataUrl ? (
                        <div className="svg-placeholder">
                            <p>
                                Upload a raster image to build a luminance
                                grid.
                            </p>
                        </div>
                    ) : (
                        <div className="svg-container svg-container--fill">
                            <svg
                                ref={svgRef}
                                viewBox={viewBox}
                                xmlns="http://www.w3.org/2000/svg"
                                className="luminance-grid-svg"
                                style={{ touchAction: "none" }}
                                shapeRendering={
                                    cellShapeUsesPaths
                                        ? "geometricPrecision"
                                        : "crispEdges"
                                }
                                {...panHandlers}
                            >
                                <g className="luminance-grid">
                                    {cells.map((c, i) =>
                                        c.pathD ? (
                                            <path
                                                key={i}
                                                d={c.pathD}
                                                fill={c.fill}
                                            />
                                        ) : (
                                            <rect
                                                key={i}
                                                x={c.x}
                                                y={c.y}
                                                width={1}
                                                height={1}
                                                fill={c.fill}
                                            />
                                        )
                                    )}
                                </g>
                                {blobOutlineEnabled &&
                                    blobOutlineLayerGroups
                                        .filter((g) => g.paths.length > 0)
                                        .map((group) => (
                                            <g
                                                key={group.id}
                                                className="luminance-grid-blobs"
                                                fill="none"
                                                stroke={group.strokeColor}
                                                strokeWidth={group.strokeWidth}
                                                strokeLinejoin="round"
                                                strokeLinecap="round"
                                                shapeRendering="geometricPrecision"
                                            >
                                                {group.paths.map((d, i) => (
                                                    <path
                                                        key={`${group.id}-${i}`}
                                                        d={d}
                                                    />
                                                ))}
                                            </g>
                                        ))}
                            </svg>
                        </div>
                    )}
                </div>
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {cells.length > 0
                            ? `Grid ${effectiveCols}×${effectiveRows} (${cells.length.toLocaleString()} cells).`
                            : "No grid yet."}
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}
